<template>
  <div class="system-preview">
    <div class="card">
      <div class="card-body">
        <div class="row justify-content-center">
          <div class="col-lg-12">
            <div class="text-center mb-4">
              <div class="icon-wrapper mb-3">
                <i class="bi bi-eye display-4 text-primary"></i>
              </div>
              <h2 class="card-title mb-2">System Preview</h2>
              <p class="text-muted">
                Review your complete configuration from localStorage
              </p>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="text-muted mt-2">Loading configuration data...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger" role="alert">
              <h4 class="alert-heading">Error Loading Data</h4>
              <p>{{ error }}</p>
              <hr />
              <p class="mb-0">
                Please check your browser console for more details.
              </p>
            </div>

            <!-- Main Content -->
            <div v-else>
              <!-- Configuration Summary Cards -->
              <div class="row mb-4">
                <!-- Subject Configuration Summary -->
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="card border-primary h-100">
                    <div class="card-header bg-primary text-white">
                      <h6 class="mb-0">
                        <i class="bi bi-gear me-2"></i>PsySection Configuration
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="summary-item">
                        <small class="text-muted">PsySection Name:</small>
                        <div class="fw-semibold">
                          {{ subject.subjectName || "Not Set" }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Competencies Summary -->
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="card border-success h-100">
                    <div class="card-header bg-success text-white">
                      <h6 class="mb-0">
                        <i class="bi bi-trophy me-2"></i>Competencies
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="summary-item">
                        <small class="text-muted">Total Competencies:</small>
                        <div class="fw-semibold">{{ competencies.length }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Traits Summary -->
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="card border-info h-100">
                    <div class="card-header bg-info text-white">
                      <h6 class="mb-0">
                        <i class="bi bi-sliders me-2"></i>Traits
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="summary-item">
                        <small class="text-muted">Total Traits:</small>
                        <div class="fw-semibold">{{ traits.length }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Question Rules Summary -->
                <div class="col-lg-3 col-md-6 mb-3">
                  <div class="card border-warning h-100">
                    <div class="card-header bg-warning text-white">
                      <h6 class="mb-0">
                        <i class="bi bi-question-circle me-2"></i>Question Rules
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="summary-item">
                        <small class="text-muted">Total Rules:</small>
                        <div class="fw-semibold">
                          {{ questionRules.length }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Configuration Tree Structure -->
              <div class="row mb-4">
                <div class="col-12">
                  <div class="card">
                    <div
                      class="card-header d-flex justify-content-between align-items-center"
                    >
                      <h5 class="mb-0">
                        <i class="bi bi-diagram-3 me-2"></i>Configuration Tree
                        Structure
                      </h5>
                      <div class="btn-group btn-group-sm" role="group">
                        <button
                          type="button"
                          class="btn btn-outline-primary"
                          @click="expandAll"
                        >
                          Expand All
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-secondary"
                          @click="collapseAll"
                        >
                          Collapse All
                        </button>
                      </div>
                    </div>
                    <div class="card-body">
                      <!-- No Data State -->
                      <div
                        v-if="buildTreeStructure.length === 0"
                        class="text-center text-muted py-4"
                      >
                        <i class="bi bi-info-circle display-6 mb-3"></i>
                        <p>No configuration data found in localStorage.</p>
                        <p class="small">
                          Please complete the configuration steps first.
                        </p>
                      </div>

                      <!-- Tree Container -->
                      <div v-else class="tree-container">
                        <!-- Subject Root Node -->
                        <div class="tree-root">
                          <div class="root-header">
                            <i class="bi bi-book text-primary me-2"></i>
                            <strong
                              >PsySection:
                              {{
                                subject.subjectName || "Unknown PsySection"
                              }}</strong
                            >
                          </div>

                          <!-- Competencies Level -->
                          <div class="tree-children">
                            <div
                              v-for="(
                                competency, compIndex
                              ) in buildTreeStructure"
                              :key="'comp-' + competency.id"
                              class="tree-node competency-level"
                            >
                              <div class="node-line">
                                <div class="tree-connector">
                                  <span
                                    class="connector-line"
                                    v-html="
                                      compIndex ===
                                      buildTreeStructure.length - 1
                                        ? '└──'
                                        : '├──'
                                    "
                                  ></span>
                                </div>
                                <div
                                  class="node-content"
                                  @click="
                                    toggleNode('competency', competency.id)
                                  "
                                >
                                  <i
                                    v-if="competency.traits.length > 0"
                                    class="bi expand-icon me-2"
                                    :class="
                                      isNodeExpanded(
                                        'competency',
                                        competency.id
                                      )
                                        ? 'bi-chevron-down'
                                        : 'bi-chevron-right'
                                    "
                                  ></i>
                                  <i v-else class="expand-spacer"></i>
                                  <i class="bi bi-trophy text-primary me-2"></i>
                                  <span class="node-title"
                                    >Competency: {{ competency.name }}</span
                                  >
                                  <span class="node-id text-muted"
                                    >(ID: {{ competency.id }})</span
                                  >
                                </div>
                              </div>

                              <!-- Traits Level -->
                              <div
                                v-if="
                                  isNodeExpanded('competency', competency.id)
                                "
                                class="tree-children"
                              >
                                <div
                                  v-for="(
                                    trait, traitIndex
                                  ) in competency.traits"
                                  :key="
                                    'trait-' +
                                    trait.id +
                                    '-' +
                                    trait.competencyId
                                  "
                                  class="tree-node trait-level"
                                >
                                  <div class="node-line">
                                    <div class="tree-connector">
                                      <span
                                        class="connector-line"
                                        v-html="
                                          (compIndex ===
                                          buildTreeStructure.length - 1
                                            ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                            : '│&nbsp;&nbsp;&nbsp;') +
                                          (traitIndex ===
                                          competency.traits.length - 1
                                            ? '└──'
                                            : '├──')
                                        "
                                      ></span>
                                    </div>
                                    <div
                                      class="node-content"
                                      @click="
                                        toggleNode(
                                          'trait',
                                          trait.id + '-' + trait.competencyId
                                        )
                                      "
                                    >
                                      <i
                                        v-if="trait.categories.length > 0"
                                        class="bi expand-icon me-2"
                                        :class="
                                          isNodeExpanded(
                                            'trait',
                                            trait.id + '-' + trait.competencyId
                                          )
                                            ? 'bi-chevron-down'
                                            : 'bi-chevron-right'
                                        "
                                      ></i>
                                      <i v-else class="expand-spacer"></i>
                                      <i
                                        class="bi bi-sliders text-info me-2"
                                      ></i>
                                      <span class="node-title"
                                        >Trait: {{ trait.name }}</span
                                      >
                                      <span class="node-id text-muted"
                                        >(ID: {{ trait.id }})</span
                                      >
                                    </div>
                                  </div>

                                  <!-- Categories Level -->
                                  <div
                                    v-if="
                                      isNodeExpanded(
                                        'trait',
                                        trait.id + '-' + trait.competencyId
                                      )
                                    "
                                    class="tree-children"
                                  >
                                    <div
                                      v-for="(
                                        category, categoryIndex
                                      ) in trait.categories"
                                      :key="'cat-' + category.id"
                                      class="tree-node category-level"
                                    >
                                      <div class="node-line">
                                        <div class="tree-connector">
                                          <span
                                            class="connector-line"
                                            v-html="
                                              (compIndex ===
                                              buildTreeStructure.length - 1
                                                ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                : '│&nbsp;&nbsp;&nbsp;') +
                                              (traitIndex ===
                                              competency.traits.length - 1
                                                ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                : '│&nbsp;&nbsp;&nbsp;') +
                                              (categoryIndex ===
                                              trait.categories.length - 1
                                                ? '└──'
                                                : '├──')
                                            "
                                          ></span>
                                        </div>
                                        <div
                                          class="node-content"
                                          @click="
                                            toggleNode('category', category.id)
                                          "
                                        >
                                          <i
                                            v-if="category.rules.length > 0"
                                            class="bi expand-icon me-2"
                                            :class="
                                              isNodeExpanded(
                                                'category',
                                                category.id
                                              )
                                                ? 'bi-chevron-down'
                                                : 'bi-chevron-right'
                                            "
                                          ></i>
                                          <i v-else class="expand-spacer"></i>
                                          <i
                                            class="bi bi-tags text-success me-2"
                                          ></i>
                                          <span class="node-title"
                                            >Category: {{ category.name }}</span
                                          >
                                          <span class="node-id text-muted"
                                            >(ID: {{ category.id }})</span
                                          >
                                        </div>
                                      </div>

                                      <!-- Question Rules Level -->
                                      <div
                                        v-if="
                                          isNodeExpanded(
                                            'category',
                                            category.id
                                          )
                                        "
                                        class="tree-children"
                                      >
                                        <div
                                          v-for="(
                                            rule, ruleIndex
                                          ) in category.rules"
                                          :key="'rule-' + rule.id"
                                          class="tree-node rule-level"
                                        >
                                          <div class="node-line">
                                            <div class="tree-connector">
                                              <span
                                                class="connector-line"
                                                v-html="
                                                  (compIndex ===
                                                  buildTreeStructure.length - 1
                                                    ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                    : '│&nbsp;&nbsp;&nbsp;') +
                                                  (traitIndex ===
                                                  competency.traits.length - 1
                                                    ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                    : '│&nbsp;&nbsp;&nbsp;') +
                                                  (categoryIndex ===
                                                  trait.categories.length - 1
                                                    ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                    : '│&nbsp;&nbsp;&nbsp;') +
                                                  (ruleIndex ===
                                                  category.rules.length - 1
                                                    ? '└──'
                                                    : '├──')
                                                "
                                              ></span>
                                            </div>
                                            <div class="node-content">
                                              <i
                                                class="bi bi-question-circle text-warning me-2"
                                              ></i>
                                              <span class="node-title"
                                                >Question Rule:
                                                {{ rule.name }}</span
                                              >
                                            </div>
                                          </div>

                                          <!-- Rule Details -->
                                          <div class="rule-details">
                                            <div class="detail-line">
                                              <div class="tree-connector">
                                                <span
                                                  class="connector-line"
                                                  v-html="
                                                    (compIndex ===
                                                    buildTreeStructure.length -
                                                      1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    (traitIndex ===
                                                    competency.traits.length - 1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    (categoryIndex ===
                                                    trait.categories.length - 1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    (ruleIndex ===
                                                    category.rules.length - 1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    '├──'
                                                  "
                                                ></span>
                                              </div>
                                              <span class="detail-content">
                                                <i
                                                  class="bi bi-speedometer2 text-info me-1"
                                                ></i>
                                                Difficulty:
                                                {{ rule.difficulty }}
                                              </span>
                                            </div>
                                            <div class="detail-line">
                                              <div class="tree-connector">
                                                <span
                                                  class="connector-line"
                                                  v-html="
                                                    (compIndex ===
                                                    buildTreeStructure.length -
                                                      1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    (traitIndex ===
                                                    competency.traits.length - 1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    (categoryIndex ===
                                                    trait.categories.length - 1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    (ruleIndex ===
                                                    category.rules.length - 1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    '├──'
                                                  "
                                                ></span>
                                              </div>
                                              <span class="detail-content">
                                                <i
                                                  class="bi bi-hash text-secondary me-1"
                                                ></i>
                                                Type: {{ rule.type }}
                                              </span>
                                            </div>
                                            <div
                                              class="detail-line"
                                              v-if="
                                                rule.fixedQuestionIds &&
                                                rule.fixedQuestionIds.length > 0
                                              "
                                            >
                                              <div class="tree-connector">
                                                <span
                                                  class="connector-line"
                                                  v-html="
                                                    (compIndex ===
                                                    buildTreeStructure.length -
                                                      1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    (traitIndex ===
                                                    competency.traits.length - 1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    (categoryIndex ===
                                                    trait.categories.length - 1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    (ruleIndex ===
                                                    category.rules.length - 1
                                                      ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                      : '│&nbsp;&nbsp;&nbsp;') +
                                                    '└──'
                                                  "
                                                ></span>
                                              </div>
                                              <span class="detail-content">
                                                <i
                                                  class="bi bi-list-ol text-warning me-1"
                                                ></i>
                                                Questions: [{{
                                                  rule.fixedQuestionIds.join(
                                                    ", "
                                                  )
                                                }}]
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        <!-- No rules message -->
                                        <div
                                          v-if="category.rules.length === 0"
                                          class="tree-node empty-rule"
                                        >
                                          <div class="node-line">
                                            <div class="tree-connector">
                                              <span
                                                class="connector-line"
                                                v-html="
                                                  (compIndex ===
                                                  buildTreeStructure.length - 1
                                                    ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                    : '│&nbsp;&nbsp;&nbsp;') +
                                                  (traitIndex ===
                                                  competency.traits.length - 1
                                                    ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                    : '│&nbsp;&nbsp;&nbsp;') +
                                                  (categoryIndex ===
                                                  trait.categories.length - 1
                                                    ? '&nbsp;&nbsp;&nbsp;&nbsp;'
                                                    : '│&nbsp;&nbsp;&nbsp;') +
                                                  '└──'
                                                "
                                              ></span>
                                            </div>
                                            <div
                                              class="node-content text-muted"
                                            >
                                              <i
                                                class="bi bi-exclamation-triangle me-2"
                                              ></i>
                                              <span class="node-title"
                                                >No question rules
                                                configured</span
                                              >
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex justify-content-between align-items-center">
                <button class="btn btn-outline-secondary" @click="goBack">
                  <i class="bi bi-arrow-left me-2"></i>Previous
                </button>

                <div class="d-flex gap-2">
                  <button class="btn btn-outline-info" @click="refreshData">
                    <i class="bi bi-arrow-clockwise me-2"></i>Refresh Data
                  </button>
                  <button
                    class="btn btn-outline-info"
                    @click="exportConfiguration"
                  >
                    <i class="bi bi-download me-2"></i>Export Config
                  </button>
                  <button class="btn btn-success" @click="finishConfiguration">
                    <i class="bi bi-check-lg me-2"></i>Finish Setup
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";

// Reactive variables
const loading = ref(true);
const error = ref(null);
const expandedNodes = ref({});

const competencies = ref([]);
const traits = ref([]);
const categories = ref([]);
const questionRules = ref([]);
const subject = ref({ subjectName: "Testing" });

// Helper functions
const getTraitsByCompetency = (competencyId) => {
  return traits.value.filter((t) => t.competencyId === competencyId);
};

const getCategoriesByTrait = (traitId) => {
  return categories.value.filter((c) => c.traitId === traitId);
};

const getRulesByCategory = (categoryId) => {
  return questionRules.value.filter((r) => r.categoryId === categoryId);
};

// Computed property to build tree structure
const buildTreeStructure = computed(() => {
  console.log("Building tree structure...");
  const structure = [];

  // Get all competencies
  competencies.value.forEach((competency) => {
    const competencyNode = {
      id: competency.id,
      name: competency.name,
      traits: [],
    };

    // Get traits for this competency
    const competencyTraits = getTraitsByCompetency(competency.id);

    competencyTraits.forEach((trait) => {
      const traitNode = {
        id: trait.id,
        competencyId: trait.competencyId,
        name: trait.name,
        categories: [],
      };

      // Get categories for this trait
      const traitCategories = getCategoriesByTrait(trait.id);

      traitCategories.forEach((category) => {
        const categoryNode = {
          id: category.id,
          traitId: category.traitId,
          name: category.name,
          rules: [],
        };

        // Get question rules for this category
        const categoryRules = getRulesByCategory(category.id);

        categoryRules.forEach((rule) => {
          categoryNode.rules.push({
            id: rule.id,
            categoryId: rule.categoryId,
            name: rule.name,
            type: rule.type,
            difficulty: rule.difficulty,
            fixedQuestionIds: rule.fixedQuestionIds || [],
          });
        });

        traitNode.categories.push(categoryNode);
      });

      competencyNode.traits.push(traitNode);
    });

    structure.push(competencyNode);
  });

  console.log("Built tree structure:", structure);
  return structure;
});

// Tree management methods
const toggleNode = (type, id) => {
  const key = `${type}-${id}`;
  console.log("Toggling node:", key);
  expandedNodes.value[key] = !expandedNodes.value[key];
};

const isNodeExpanded = (type, id) => {
  const key = `${type}-${id}`;
  return !!expandedNodes.value[key];
};

const expandAll = () => {
  console.log("Expanding all nodes...");
  const newExpandedNodes = {};

  // Expand all competencies
  buildTreeStructure.value.forEach((competency) => {
    newExpandedNodes[`competency-${competency.id}`] = true;

    // Expand all traits for this competency
    competency.traits.forEach((trait) => {
      newExpandedNodes[`trait-${trait.id}-${trait.competencyId}`] = true;

      // Expand all categories for this trait
      trait.categories.forEach((category) => {
        newExpandedNodes[`category-${category.id}`] = true;
      });
    });
  });

  expandedNodes.value = newExpandedNodes;
};

const collapseAll = () => {
  console.log("Collapsing all nodes...");
  expandedNodes.value = {};
};

// Action methods
const refreshData = async () => {
  loading.value = true;
  error.value = null;
  await loadData();
  showNotification("Data refreshed successfully!", "success");
};

const goBack = () => {
  console.log("Going back to previous step");
  // Implement navigation logic
};

const exportConfiguration = () => {
  const config = {
    metadata: {
      exportDate: new Date().toISOString(),
      version: "1.0",
    },
    configuration: {
      subject: subject.value,
      competencies: competencies.value,
      traits: traits.value,
      categories: categories.value,
      questionRules: questionRules.value,
    },
  };

  const dataStr = JSON.stringify(config, null, 2);
  const dataBlob = new Blob([dataStr], { type: "application/json" });
  const url = URL.createObjectURL(dataBlob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `competency-framework-config-${
    new Date().toISOString().split("T")[0]
  }.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  showNotification("Configuration exported successfully!", "success");
};

const finishConfiguration = () => {
  showNotification("Configuration completed successfully!", "success");
  console.log("Configuration finished");
};

const showNotification = (message, type = "info") => {
  const alertClass =
    {
      success: "alert-success",
      error: "alert-danger",
      info: "alert-info",
      warning: "alert-warning",
    }[type] || "alert-info";

  const notification = document.createElement("div");
  notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
  notification.style.cssText =
    "top: 20px; right: 20px; z-index: 9999; min-width: 300px;";
  notification.innerHTML = `
    ${message}
    <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
  `;

  document.body.appendChild(notification);

  setTimeout(() => {
    if (notification.parentNode) {
      notification.parentNode.removeChild(notification);
    }
  }, 5000);
};

// Load data from localStorage
const loadData = async () => {
  try {
    loading.value = true;
    error.value = null;

    // Load data from localStorage
    const traitConfig = JSON.parse(localStorage.getItem("traitConfiguration"));
    const categorySetup = JSON.parse(localStorage.getItem("categoriesSetup"));
    const questionRulesSetup = JSON.parse(
      localStorage.getItem("questionRulesSetup")
    );
    const subjectConfig = JSON.parse(
      localStorage.getItem("subjectConfiguration")
    );

    // Set the data
    competencies.value = traitConfig?.competencies || [];
    traits.value = traitConfig?.traits || [];
    categories.value = categorySetup?.categories || [];
    questionRules.value = questionRulesSetup?.questionRules || [];
    subject.value = subjectConfig || { subjectName: "Testing" };

    console.log("✅ Data loaded from localStorage");
    console.log("Competencies:", competencies.value);
    console.log("Traits:", traits.value);
    console.log("Categories:", categories.value);
    console.log("Question Rules:", questionRules.value);

    // Auto-expand first level
    const initialExpanded = {};
    buildTreeStructure.value.forEach((competency) => {
      initialExpanded[`competency-${competency.id}`] = true;
    });
    expandedNodes.value = initialExpanded;
  } catch (err) {
    console.error("❌ Failed to load data:", err);
    error.value = `Failed to load configuration data: ${err.message}`;
  } finally {
    loading.value = false;
  }
};

// Load data on component mount
onMounted(() => {
  loadData();
});
</script>

<style scoped>
.system-preview {
  padding: 2rem 0;
}

.card {
  border: none;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #8b5cf6, #a855f7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: white;
}

.summary-item {
  margin-bottom: 0.75rem;
}

.summary-item:last-child {
  margin-bottom: 0;
}

/* Tree Structure Styles */
.tree-container {
  font-family: "Courier New", Monaco, monospace;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  max-height: 600px;
  overflow-y: auto;
}

.tree-root {
  margin-bottom: 1rem;
}

.root-header {
  font-size: 1.1rem;
  padding: 0.75rem;
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  border-radius: 6px;
  border-left: 4px solid #2196f3;
  margin-bottom: 1rem;
}

.tree-children {
  margin-left: 0;
}

.tree-node {
  margin-bottom: 0.25rem;
}

.node-line {
  display: flex;
  align-items: flex-start;
  min-height: 1.5rem;
}

.tree-connector {
  color: #6c757d;
  font-weight: bold;
  white-space: pre-wrap;
  font-size: 0.9rem;
  line-height: 1.5;
  min-width: fit-content;
  font-family: monospace;
}

.connector-line {
  user-select: none;
  display: inline-block;
}

.node-content {
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  background: white;
  border-radius: 4px;
  margin-left: 0.5rem;
  border: 1px solid #dee2e6;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-height: 2rem;
}

.node-content:hover {
  background: #f8f9fa;
  border-color: #8b5cf6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.competency-level .node-content {
  border-left: 3px solid #2196f3;
  background: linear-gradient(135deg, #e3f2fd, #ffffff);
}

.trait-level .node-content {
  border-left: 3px solid #00bcd4;
  background: linear-gradient(135deg, #e0f2f1, #ffffff);
}

.category-level .node-content {
  border-left: 3px solid #4caf50;
  background: linear-gradient(135deg, #e8f5e8, #ffffff);
}

.rule-level .node-content {
  border-left: 3px solid #ff9800;
  background: linear-gradient(135deg, #fff3e0, #ffffff);
  cursor: default;
}

.expand-icon {
  cursor: pointer;
  color: #6c757d;
  font-size: 0.875rem;
  width: 16px;
  text-align: center;
}

.expand-spacer {
  width: 16px;
  margin-right: 0.5rem;
}

.node-title {
  font-weight: 600;
  color: #333;
  margin-right: 0.5rem;
}

.node-id {
  font-size: 0.8rem;
  font-style: italic;
}

.rule-details {
  margin-left: 0;
  margin-top: 0.25rem;
}

.detail-line {
  display: flex;
  align-items: center;
  margin-bottom: 0.125rem;
  min-height: 1.25rem;
}

.detail-content {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #495057;
  padding: 0.125rem 0.5rem;
  background: #f8f9fa;
  border-radius: 3px;
  margin-left: 0.5rem;
  border: 1px solid #e9ecef;
}

.empty-rule .node-content {
  background: #f8f9fa;
  border: 1px dashed #dee2e6;
  color: #6c757d;
  cursor: default;
}

.empty-rule .node-content:hover {
  background: #f8f9fa;
  border-color: #dee2e6;
  box-shadow: none;
}

.btn {
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.d-flex.gap-2 {
  gap: 0.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .tree-container {
    font-size: 0.85rem;
    padding: 1rem;
  }

  .tree-connector {
    font-size: 0.8rem;
  }

  .node-content {
    padding: 0.25rem 0.5rem;
    min-height: 1.75rem;
  }

  .node-title {
    font-size: 0.9rem;
  }

  .node-id {
    font-size: 0.75rem;
  }

  .detail-content {
    font-size: 0.8rem;
    padding: 0.1rem 0.375rem;
  }

  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }

  .d-flex.gap-2 {
    flex-direction: column;
    gap: 0.5rem;
  }

  .btn {
    width: 100%;
  }
}

/* Custom scrollbar for tree container */
.tree-container::-webkit-scrollbar {
  width: 8px;
}

.tree-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.tree-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.tree-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
